<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import naviappInboxes from 'dashboard/api/naviappInboxes';
import { isValidApiKey } from '../helpers/naviapp';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();

const apiKey = ref('');
const reconnecting = ref(false);
const errorMessage = ref('');

const canReconnect = computed(() => isValidApiKey(apiKey.value.trim()));

const reconnect = async () => {
  if (!canReconnect.value || reconnecting.value) return;
  reconnecting.value = true;
  errorMessage.value = '';
  try {
    await naviappInboxes.update(props.inbox.id, {
      api_key: apiKey.value.trim(),
    });
    await store.dispatch('inboxes/get');
    apiKey.value = '';
    useAlert(t('INBOX_MGMT.NAVIAPP_SETTINGS.RECONNECT_SUCCESS'));
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      t('INBOX_MGMT.NAVIAPP_SETTINGS.RECONNECT_ERROR');
  } finally {
    reconnecting.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-6 max-w-2xl py-4">
    <section class="flex flex-col gap-3">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ t('INBOX_MGMT.NAVIAPP_SETTINGS.TITLE') }}
      </h3>
      <p
        v-if="inbox.reauthorization_required"
        data-test="naviapp-reauth"
        class="p-3 text-sm rounded-lg bg-n-ruby-3 text-n-ruby-11"
      >
        {{ t('INBOX_MGMT.NAVIAPP_SETTINGS.REAUTH_REQUIRED') }}
      </p>
      <dl class="grid grid-cols-1 gap-2 text-sm sm:grid-cols-[10rem_1fr]">
        <dt class="text-n-slate-11">
          {{ t('INBOX_MGMT.NAVIAPP_SETTINGS.TENANT_ID') }}
        </dt>
        <dd class="font-mono break-all text-n-slate-12">
          {{ inbox.naviapp_tenant_id }}
        </dd>
        <dt class="text-n-slate-11">
          {{ t('INBOX_MGMT.NAVIAPP_SETTINGS.API_KEY') }}
        </dt>
        <dd class="font-mono text-n-slate-12">
          {{ inbox.naviapp_api_key_masked }}
        </dd>
      </dl>
      <div
        v-if="inbox.naviapp_test_mode"
        data-test="naviapp-test-mode"
        class="flex flex-col gap-1 p-3 text-sm rounded-lg bg-n-amber-3 text-n-amber-11"
      >
        <span class="font-medium">
          {{ t('INBOX_MGMT.NAVIAPP_SETTINGS.TEST_MODE') }}
        </span>
        <span>{{ t('INBOX_MGMT.NAVIAPP_SETTINGS.TEST_MODE_HINT') }}</span>
      </div>
    </section>

    <section class="flex flex-col gap-3">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ t('INBOX_MGMT.NAVIAPP_SETTINGS.RECONNECT_TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11">
        {{ t('INBOX_MGMT.NAVIAPP_SETTINGS.RECONNECT_DESC') }}
      </p>
      <form class="flex flex-col gap-3 max-w-md" @submit.prevent="reconnect">
        <Input
          v-model="apiKey"
          type="password"
          :label="t('INBOX_MGMT.NAVIAPP_SETTINGS.NEW_KEY_LABEL')"
          :placeholder="t('INBOX_MGMT.NAVIAPP_SETTINGS.NEW_KEY_PLACEHOLDER')"
        />
        <p
          v-if="errorMessage"
          data-test="naviapp-reconnect-error"
          class="text-sm text-n-ruby-11"
        >
          {{ errorMessage }}
        </p>
        <div>
          <Button
            type="submit"
            :label="t('INBOX_MGMT.NAVIAPP_SETTINGS.RECONNECT')"
            :is-loading="reconnecting"
            :disabled="!canReconnect || reconnecting"
          />
        </div>
      </form>
    </section>
  </div>
</template>
